// Types for local chore management based on requirements spec

export type UUID = string;
export type Timestamp = string; // ISO string

export type DayOfWeek =
  | 'monday' | 'tuesday' | 'wednesday' | 'thursday'
  | 'friday' | 'saturday' | 'sunday';

export type Schedule =
  /** Every `interval` minutes — but only ever a whole number of days (see
   * scheduleUtils.MIN_INTERVAL_MINUTES). Minutes are the stored unit for historical reasons: it is
   * what the sync payload and the legacy `interval_frequency` column carry. Anything shorter than
   * a day cannot be expressed, because a status built on whole calendar days cannot tell an hourly
   * chore from a daily one; rows written while hours were still offered read as a single day. */
  | { type: 'minutes'; interval: number }
  | { type: 'weekly'; days: DayOfWeek[]; interval?: number }
  | { type: 'monthly'; dayOfMonth: number; interval?: number }
  /** The Nth occurrence of `weekday` in the month, e.g. ordinal 1 + weekday 'monday' is "the first
   * Monday". Ordinal -1 means the last occurrence instead of counting from the start, so "last
   * Friday" doesn't need to know how many Fridays a given month has. */
  | { type: 'monthlyWeekday'; weekday: DayOfWeek; ordinal: 1 | 2 | 3 | 4 | -1; interval?: number }
  | { type: 'yearly'; month: number; dayOfMonth: number }
  | { type: 'once'; dueDate: string };

/** The kinds of schedule a user picks between. 'monthlyWeekday' isn't one of its own — it's a
 * mode of "Monthly" — so it's grouped under 'monthly' wherever schedules are chosen or filtered. */
export type ScheduleKind = Exclude<Schedule['type'], 'monthlyWeekday'>;

export const SCHEDULE_KIND_LABELS: Record<ScheduleKind, string> = {
  minutes: 'Interval',
  weekly: 'Weekly',
  monthly: 'Monthly',
  yearly: 'Yearly',
  once: 'One-time',
};

export const scheduleKind = (schedule: Schedule): ScheduleKind =>
  schedule.type === 'monthlyWeekday' ? 'monthly' : schedule.type;

export interface Chore {
  id: UUID;
  name: string;
  description?: string;
  labels?: string[];
  schedule: Schedule;
  household_id: UUID;
  created_at: Timestamp;
  assigned_to: ChoreAssignment[];
  last_completed_at?: Timestamp;
  last_completed_by?: UUID;
}

export interface Activity {
  id: UUID;
  chore_id: UUID;
  completed_at: Timestamp;
  by_user: UUID;
}

export interface ChoreAssignment {
  userId: UUID;
  isResponsible: boolean;
}

export interface HouseholdMember {
  id: UUID;
  name: string;
  email?: string;
}

export interface Household {
  id: UUID;
  name: string;
  created_at: Timestamp;
  owner_id: UUID;
  owner_display_name: string;
  member_ids: UUID[];
  member_display_names: string[];
  /** The members who are children: they see only the chores assigned to them, and only tick them off. */
  child_ids?: UUID[];
}

export interface HouseholdAggregate {
  household: Household;
  members: HouseholdMember[];
  chores: ChoreAggregate[];
}

export interface ChoreAggregate
{
  chore: Chore;
  household: Household;
  activities: Activity[];
}

export enum HouseholdRole {
  Admin = 'admin',
  Member = 'member',
}

export enum ChoreStatus {
  Overdue = 'overdue',
  DueToday = 'due_today',
  Upcoming = 'upcoming',
}



// Computed types for UI
export interface ChoreWithDetails extends Chore {
  household: Household;
  //assignments: ChoreAssignment[];
  lastActivity?: Activity;
  nextDue: Date;
  status: ChoreStatus;
  daysUntilDue: number;
  percentageUntilDue: number;
}

export const SyncActionTypes = {
  CHORE_COMPLETED: 'ChoreCompleted',
  CHORE_CREATED: 'ChoreCreated',
  CHORE_DELETED: 'ChoreDeleted',
  CHORE_UPDATED: 'ChoreUpdated',
  HOUSEHOLD_CREATED: 'HouseholdCreated',
  HOUSEHOLD_DELETED: 'HouseholdDeleted',
} as const;

export type SyncActionType = typeof SyncActionTypes[keyof typeof SyncActionTypes];

export interface OfflineQueueItem {
  id?: number;
  timestamp: Timestamp;
  type: SyncActionType;
  choreId?: UUID;  // For chore actions
  householdId?: UUID;  // For household actions
  data: Record<string, unknown>;
  lastError?: string;
  /** Set on a reported failure the server refused outright (isRefusedByServer): already gone from
   * the queue, so it's never pushed again, only shown until the report is cleared. */
  refused?: boolean;
}

export type InvitationStatus = 'pending' | 'accepted' | 'rejected';

export interface HouseholdInvitation {
  id: UUID;
  household_id: UUID;
  household_name: string;
  invited_by_uid: UUID;
  invited_by_name: string;
  invited_email: string;
  invited_uid?: UUID;
  /** Joins as one of the household's children (docs/plans/children.md). */
  as_child?: boolean;
  status: InvitationStatus;
  created_at: Timestamp;
  updated_at: Timestamp;
}